// Per-100g nutrient math for the Catalog Manager food widgets.
//
// Passio stores nutrients **per 100g**. To show values for a chosen portion we
// scale every nutrient by `scaleFactor = portion.weight.value * quantity / 100`.
// This mirrors the canonical logic in the mealplan-app
// (`utils/nutritionCalculations.ts` -> `calculateNutritionForPortionDirect`).
import { PassioFood, PassioNutrient, PassioPortion } from "./types";

/** Macro short names we surface in the summary row. */
export const MACRO_SHORTNAMES = {
  calories: ["calories", "energy"],
  protein: ["protein"],
  carbs: ["carbs", "carbohydrate"],
  fat: ["fat", "lipid"],
  fiber: ["fiber", "fibre"],
} as const;

export type MacroKey = keyof typeof MACRO_SHORTNAMES;

export interface ScaledNutrient {
  name: string;
  shortName?: string;
  unit: string;
  /** Amount already scaled to the selected portion + quantity. */
  amount: number;
  /** Raw per-100g amount (unscaled). */
  per100g: number;
}

/** Effective default quantity for a portion (prefers `suggestedQuantity[0]`). */
export const getEffectiveQuantity = (portion?: PassioPortion): number => {
  if (!portion) return 1;
  if (portion.suggestedQuantity && portion.suggestedQuantity.length > 0) {
    return portion.suggestedQuantity[0];
  }
  return portion.quantity || 1;
};

/** Total grams for a portion at a given quantity. */
export const getTotalGrams = (
  portion: PassioPortion | undefined,
  quantity = 1
): number => {
  const value = portion?.weight?.value;
  if (typeof value !== "number") return 0;
  return value * quantity;
};

/** Format a gram weight for display (drops trailing `.0`). */
export const formatGrams = (grams: number): string => {
  const rounded = Math.round(grams * 10) / 10;
  return rounded % 1 === 0 ? `${Math.round(rounded)} g` : `${rounded} g`;
};

/**
 * Pick a sensible default portion index. Prefers an explicit 100g portion
 * (weight 1g x suggestedQuantity 100), otherwise the first portion.
 */
export const defaultPortionIndex = (portions: PassioPortion[]): number => {
  const hundred = portions.findIndex(
    (p) =>
      p.weight?.unit === "g" &&
      p.weight?.value === 1 &&
      (p.suggestedQuantity || []).includes(100)
  );
  return hundred >= 0 ? hundred : 0;
};

const matchesShortName = (
  n: PassioNutrient,
  candidates: readonly string[]
): boolean => {
  const short = (n.nutrient?.shortName || "").toLowerCase();
  const name = (n.nutrient?.name || "").toLowerCase();
  return candidates.some((c) => short === c || name.includes(c));
};

const KJ_PER_KCAL = 4.184;

const unitIsKcal = (unit?: string): boolean => {
  const u = (unit || "").toLowerCase();
  return u === "kcal" || u === "cal" || u.includes("calorie");
};

const unitIsKj = (unit?: string): boolean => {
  const u = (unit || "").toLowerCase();
  return u.includes("kj") || u.includes("joule");
};

/**
 * Per-100g calories (kcal), unscaled. A record can carry Energy in kcal AND/OR
 * kJ (some sources store both, or only kJ), so we can't just take the first
 * "energy"/"calories" match — that's how a kJ number (e.g. 1954) ends up shown
 * as "Cals". Prefer an explicit kcal value; otherwise convert a kJ value to
 * kcal; otherwise assume the lone value is already kcal (the Passio norm).
 */
export const caloriesPer100g = (
  food: PassioFood | undefined
): number | undefined => {
  const list = food?.nutrients;
  if (!Array.isArray(list)) return undefined;
  const cals = list.filter(
    (n) =>
      matchesShortName(n, MACRO_SHORTNAMES.calories) &&
      typeof n.amount === "number"
  );
  if (cals.length === 0) return undefined;

  const kcal = cals.find((n) => unitIsKcal(n.nutrient?.unit));
  if (kcal) return kcal.amount as number;

  const kj = cals.find((n) => unitIsKj(n.nutrient?.unit));
  if (kj) return (kj.amount as number) / KJ_PER_KCAL;

  return cals[0].amount as number;
};

/** Per-100g amount of a macro (unscaled), or undefined if absent. */
export const macroPer100g = (
  food: PassioFood | undefined,
  macro: MacroKey
): number | undefined => {
  if (macro === "calories") return caloriesPer100g(food);
  const list = food?.nutrients;
  if (!Array.isArray(list)) return undefined;
  const hit = list.find((n) => matchesShortName(n, MACRO_SHORTNAMES[macro]));
  return typeof hit?.amount === "number" ? hit.amount : undefined;
};

/**
 * Scale all of a food's nutrients to the given total grams. Returns the macro
 * summary plus the full sorted nutrient list (names starting with a digit are
 * pushed to the end, matching the reference component).
 */
export const scaleNutrition = (
  food: PassioFood | undefined,
  grams: number
): {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  nutrients: ScaledNutrient[];
} => {
  const list = food?.nutrients;
  const factor = grams / 100;
  const empty = {
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    fiber: 0,
    nutrients: [] as ScaledNutrient[],
  };
  if (!Array.isArray(list) || list.length === 0) return empty;

  const macro = (key: MacroKey): number => {
    const v = macroPer100g(food, key);
    return typeof v === "number" ? v * factor : 0;
  };

  const nutrients: ScaledNutrient[] = list
    .filter((n) => n.nutrient?.name && typeof n.amount === "number")
    .map((n) => ({
      name: n.nutrient?.name || "",
      shortName: n.nutrient?.shortName,
      unit: n.nutrient?.unit || "",
      amount: (n.amount as number) * factor,
      per100g: n.amount as number,
    }))
    .sort((a, b) => {
      const aNum = /^\d/.test(a.name);
      const bNum = /^\d/.test(b.name);
      if (aNum && !bNum) return 1;
      if (!aNum && bNum) return -1;
      return a.name.localeCompare(b.name);
    });

  return {
    calories: macro("calories"),
    protein: macro("protein"),
    carbs: macro("carbs"),
    fat: macro("fat"),
    fiber: macro("fiber"),
    nutrients,
  };
};

/** Title-case a nutrient/unit name ("Total lipid (fat)" -> "Total Lipid (fat)"). */
export const prettyNutrientName = (name: string): string =>
  name
    .split(/[_\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");

/** Round a display value to at most 1 decimal and append the (lowercased) unit. */
export const formatAmount = (amount: number, unit: string): string => {
  const rounded = Math.round(amount * 10) / 10;
  const num = rounded % 1 === 0 ? String(Math.round(rounded)) : String(rounded);
  if (!unit) return num;
  return `${num} ${unit.toLowerCase()}`;
};
